import type { AxiosResponse } from 'axios'
import type { AxiosOptions } from 'shared/api'
import { type IAnalyticsExportResponse } from 'entities/Analytics/api/types'
import { baseApi } from 'entities/Auth'
import {
  type IRequestReviewsAllParams,
  type IResponseRequestReviewsAll,
  type IResponseReview,
  type IResponseReviewContacts,
  type IResponseReviewDeleteImage,
  type IResponseReviewsList,
  type IResponseReviewUploadImage,
  type IReviewAccount,
  type IReviewContactsParams,
  type IReviewDeleteImageParams,
  type IReviewExportRequestParams,
  type IReviewParams,
  type IReviewsListParams,
  type IReviewUploadImageParams,
} from 'entities/Review/api/types'

class Api {
  createReview(params: IReviewParams): Promise<AxiosResponse<{ data: IResponseReview }>> {
    return baseApi.post('reviews', params)
  }

  getReviews(
    params?: IReviewsListParams,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseReviewsList>> {
    return baseApi.get('reviews', { params, ...options })
  }

  getReviewById(
    id: string | number,
    withStats?: boolean
  ): Promise<AxiosResponse<{ data: IResponseReview }>> {
    return baseApi.get(`reviews/${id}${withStats ? '?withStats=true' : ''}`)
  }

  updateReview(id: number, params: IReviewParams): Promise<AxiosResponse<IResponseReview>> {
    return baseApi.put(`reviews/${id}`, params)
  }

  deleteReview(id: number): Promise<void> {
    return baseApi.delete(`reviews/${id}`)
  }

  uploadReviewImage(
    params: IReviewUploadImageParams
  ): Promise<AxiosResponse<IResponseReviewUploadImage>> {
    return baseApi.post('reviews/image', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  deleteReviewImage(
    params: IReviewDeleteImageParams
  ): Promise<AxiosResponse<IResponseReviewDeleteImage>> {
    return baseApi.delete('reviews/image', { data: params })
  }

  validateReviewAccount(params: IReviewAccount): Promise<AxiosResponse<IReviewAccount>> {
    return baseApi.post('reviews/accounts/validate', params)
  }

  getRequestReviewsAll(
    params?: IRequestReviewsAllParams,
    options?: AxiosOptions
  ): Promise<AxiosResponse<{ data: IResponseRequestReviewsAll }>> {
    return baseApi.get('reviews/all', { params, ...options })
  }

  getReviewContactsById(
    id: number,
    params: IReviewContactsParams,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseReviewContacts>> {
    return baseApi.get(`reviews/${id}/contacts`, { params, ...options })
  }

  exportReviewById(
    id: number,
    params: IReviewExportRequestParams,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IAnalyticsExportResponse>> {
    return baseApi.get(`reviews/${id}/export`, { params, ...options })
  }
}

export const ReviewsApi = new Api()
